import { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, TextField, Button, Stack, IconButton, Tooltip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import TitleIcon from '@mui/icons-material/Title';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import { btn, inputSx, NAVY } from '../styles/ds';
import { websiteRequest } from './websiteApi';

/*
 * Legal-page editor (WD-017). The text stays plain with a few markers — the
 * server (services/websitePageText.js) is the one that turns it into blocks and
 * rejects unsafe links, and the preview shows exactly what it returns. The
 * toolbar only inserts markers into the text:
 *   ## heading · ### sub-heading · - bullet · 1. numbered · **bold** · [text](url)
 * Nothing here ever renders HTML: blocks become React elements, text stays text.
 */

// Defense in depth: the server already only emits these, but the preview never
// renders any other href as a link.
const SAFE_HREF = /^(https:\/\/|mailto:|tel:|\/(?!\/))/i;

const Inline = ({ nodes }) => nodes.map((n, i) => {
  const text = n.bold ? <strong key={i}>{n.text}</strong> : <span key={i}>{n.text}</span>;
  if (n.type !== 'link' || !SAFE_HREF.test(n.href)) return text;
  const external = n.href.startsWith('https://');
  return (
    <a key={i} href={n.href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      style={{ color: NAVY, textDecoration: 'underline' }}>{text}</a>
  );
});

// The CRM's RTL style plugin flips left/right, so this LTR preview uses only
// logical properties (start / inline-start), which it leaves alone.
export const PageBlocks = ({ title, blocks }) => (
  <Box dir="ltr" sx={{ border: '1px solid rgba(13,29,62,0.12)', borderRadius: 2, p: 2, bgcolor: '#fff', textAlign: 'start', overflowWrap: 'anywhere' }}>
    <Typography fontWeight={700} fontSize={18} mb={1}>{title}</Typography>
    {blocks.map((b, i) => {
      if (b.type === 'heading') {
        return <Typography key={i} component={b.level === 3 ? 'h4' : 'h3'} fontWeight={700} fontSize={b.level === 3 ? 14 : 16} mt={1.5}><Inline nodes={b.content} /></Typography>;
      }
      if (b.type === 'list') {
        return (
          <Box key={i} component={b.ordered ? 'ol' : 'ul'} sx={{ my: 1, p: 0, paddingInlineStart: '24px' }}>
            {b.items.map((it, j) => <li key={j}><Typography fontSize={14} component="span"><Inline nodes={it.content} /></Typography></li>)}
          </Box>
        );
      }
      return <Typography key={i} fontSize={14} my={1}><Inline nodes={b.content} /></Typography>;
    })}
  </Box>
);

/** Server-rendered preview of the unsaved draft, refreshed shortly after typing stops. */
export function usePagePreview(slug, title, body) {
  const [preview, setPreview] = useState({ title, blocks: [], problems: [] });
  const seq = useRef(0);
  useEffect(() => {
    const mine = ++seq.current;
    const t = setTimeout(() => {
      websiteRequest(`/website_pages/${slug}/preview`, 'POST', { title_en: title, draft_body: body })
        .then(p => { if (mine === seq.current) setPreview(p); })
        .catch(err => { if (mine === seq.current) setPreview(p => ({ ...p, error: err.message })); });
    }, 300);
    return () => clearTimeout(t);
  }, [slug, title, body]);
  return preview;
}

// ── Text edits behind the toolbar ───────────────────────────────────────────

const LINE_PREFIX = /^(#{2,3} +|- +|\d{1,3}[.)] +)/;
const PREFIX = {
  heading: () => '## ',
  subheading: () => '### ',
  bullet: () => '- ',
  numbered: i => `${i + 1}. `,
};
const IS_KIND = {
  heading: l => /^## /.test(l),
  subheading: l => /^### /.test(l),
  bullet: l => /^- /.test(l),
  numbered: l => /^\d{1,3}[.)] /.test(l),
};

/** Turns the selected lines into a heading/list (or back to text when they already are). */
function toggleLines(value, s, e, kind) {
  const start = value.lastIndexOf('\n', s - 1) + 1;
  let end = value.indexOf('\n', e > s && value[e - 1] === '\n' ? e - 1 : e);
  if (end === -1) end = value.length;
  const lines = value.slice(start, end).split('\n');
  const undo = lines.every(l => !l.trim() || IS_KIND[kind](l));
  const next = lines.map((l, i) => {
    const plain = l.replace(LINE_PREFIX, '');
    return undo || !plain.trim() ? plain : PREFIX[kind](i) + plain;
  }).join('\n');
  return { text: value.slice(0, start) + next + value.slice(end), selStart: start, selEnd: start + next.length };
}

function toggleBold(value, s, e) {
  if (value.slice(s - 2, s) === '**' && value.slice(e, e + 2) === '**') {
    return { text: value.slice(0, s - 2) + value.slice(s, e) + value.slice(e + 2), selStart: s - 2, selEnd: e - 2 };
  }
  const inner = value.slice(s, e) || 'bold text';
  return { text: `${value.slice(0, s)}**${inner}**${value.slice(e)}`, selStart: s + 2, selEnd: s + 2 + inner.length };
}

const TOOLS = [
  { key: 'bold', label: 'מודגש (Ctrl+B)', icon: <FormatBoldIcon fontSize="small" /> },
  { key: 'link', label: 'קישור (Ctrl+K)', icon: <InsertLinkIcon fontSize="small" /> },
  { divider: true },
  { key: 'heading', label: 'כותרת', icon: <TitleIcon fontSize="small" /> },
  { key: 'subheading', label: 'כותרת משנה', icon: <TextFieldsIcon fontSize="small" /> },
  { key: 'bullet', label: 'רשימה עם תבליטים', icon: <FormatListBulletedIcon fontSize="small" /> },
  { key: 'numbered', label: 'רשימה ממוספרת', icon: <FormatListNumberedIcon fontSize="small" /> },
];

const LinkDialog = ({ initialText, onClose, onInsert }) => {
  const [text, setText] = useState(initialText);
  const [href, setHref] = useState('');
  // "example.com/x" → "https://example.com/x"; the server still has the final say.
  const url = /^[\w-]+(\.[\w-]+)+([/?#]|$)/.test(href.trim()) ? `https://${href.trim()}` : href.trim();
  const valid = text.trim() && SAFE_HREF.test(url) && !/[\s<>"'`\\]/.test(url) && url !== 'https://';
  return (
    <Dialog open onClose={onClose} dir="rtl" maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>הוספת קישור</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField label="טקסט הקישור" value={text} onChange={e => setText(e.target.value)} autoFocus
            inputProps={{ dir: 'ltr' }} sx={inputSx} fullWidth />
          <TextField label="כתובת" value={href} onChange={e => setHref(e.target.value)}
            placeholder="https://…" inputProps={{ dir: 'ltr' }} sx={inputSx} fullWidth
            helperText={<>
              מותר: אתר מאובטח (<bdi dir="ltr">https://…</bdi>), מייל (<bdi dir="ltr">mailto:name@example.com</bdi>),
              טלפון (<bdi dir="ltr">tel:+1…</bdi>) או דף באתר (<bdi dir="ltr">/pages/shipping-policy</bdi>).
            </>} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={btn.cancel}>ביטול</Button>
        <Button variant="contained" disabled={!valid} onClick={() => onInsert(text.trim().replace(/[[\]]/g, ''), url)} sx={btn.primary}>הוספה</Button>
      </DialogActions>
    </Dialog>
  );
};

export const PageTextEditor = ({ value, onChange, disabled }) => {
  const ref = useRef(null);
  const [link, setLink] = useState(null);         // { s, e, text } while the link dialog is open

  const apply = fn => {
    const el = ref.current;
    if (!el) return;
    const { text, selStart, selEnd } = fn(value, el.selectionStart, el.selectionEnd);
    onChange(text);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(selStart, selEnd); });
  };
  const openLink = () => {
    const el = ref.current;
    if (!el) return;
    setLink({ s: el.selectionStart, e: el.selectionEnd, text: value.slice(el.selectionStart, el.selectionEnd).split('\n')[0] });
  };
  const run = key => (key === 'bold' ? apply(toggleBold) : key === 'link' ? openLink() : apply((v, s, e) => toggleLines(v, s, e, key)));
  const onKeyDown = ev => {
    if (!(ev.ctrlKey || ev.metaKey)) return;
    const k = ev.key.toLowerCase();
    if (k === 'b' || k === 'k') { ev.preventDefault(); run(k === 'b' ? 'bold' : 'link'); }
  };
  const insertLink = (text, href) => {
    const { s, e } = link;
    setLink(null);
    const md = `[${text}](${href})`;
    apply(v => ({ text: v.slice(0, s) + md + v.slice(e), selStart: s + md.length, selEnd: s + md.length }));
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap role="toolbar" aria-label="עיצוב טקסט"
        sx={{ border: '1px solid rgba(13,29,62,0.15)', borderRadius: 2, px: 0.5, py: 0.25, mb: 1.5, bgcolor: '#f7f5f0' }}>
        {TOOLS.map((t, i) => (t.divider
          ? <Divider key={i} orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          : (
            <Tooltip key={t.key} title={t.label}>
              <span><IconButton size="small" aria-label={t.label} disabled={disabled} onClick={() => run(t.key)}>{t.icon}</IconButton></span>
            </Tooltip>
          )))}
      </Stack>
      <TextField label="טקסט הדף (אנגלית)" value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
        inputRef={ref} onKeyDown={onKeyDown} multiline minRows={12} inputProps={{ dir: 'ltr' }} sx={inputSx} fullWidth />
      {link && <LinkDialog initialText={link.text} onClose={() => setLink(null)} onInsert={insertLink} />}
    </Box>
  );
};
